'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import siteTheme from '@/lib/theme';
const { colors, fonts } = siteTheme;

interface Props {
  images: string[];
  roomName: string;
}

/* ── Lightbox ── */
function Lightbox({ images, startIndex, onClose }: { images: string[]; startIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowLeft')   prev();
      if (e.key === 'ArrowRight')  next();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, prev, next]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.94)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute', top: '1.8rem', right: '2rem',
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#fff', fontSize: '2.4rem', lineHeight: 1, opacity: 0.7,
          fontFamily: fonts.sans, fontWeight: 300, zIndex: 1,
        }}
        aria-label="Close gallery"
      >✕</button>

      {/* Counter */}
      <p style={{
        position: 'absolute', top: '2rem', left: '50%', transform: 'translateX(-50%)',
        fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(255,255,255,0.55)',
        letterSpacing: '0.12em',
      }}>
        {idx + 1} / {images.length}
      </p>

      {/* Main image */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(90vw, 120rem)',
          height: 'min(70vh, 80rem)',
          flexShrink: 0,
        }}
      >
        <Image
          src={images[idx]}
          alt={`Image ${idx + 1}`}
          fill
          sizes="90vw"
          style={{ objectFit: 'contain' }}
          priority
        />
      </div>

      {/* Prev / Next */}
      <button
        onClick={e => { e.stopPropagation(); prev(); }}
        style={{
          position: 'absolute', left: 'clamp(0.8rem, 3vw, 3rem)', top: '50%', transform: 'translateY(-50%)',
          width: '4rem', height: '4rem', borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', backdropFilter: 'blur(8px)',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
        aria-label="Previous image"
      >
        <ChevronLeft size={16} color="#fff" strokeWidth={1.6} />
      </button>
      <button
        onClick={e => { e.stopPropagation(); next(); }}
        style={{
          position: 'absolute', right: 'clamp(0.8rem, 3vw, 3rem)', top: '50%', transform: 'translateY(-50%)',
          width: '4rem', height: '4rem', borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', backdropFilter: 'blur(8px)',
          transition: 'background 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
        aria-label="Next image"
      >
        <ChevronRight size={16} color="#fff" strokeWidth={1.6} />
      </button>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: '1.8rem',
            display: 'flex', gap: '0.6rem',
            maxWidth: '90vw', overflowX: 'auto',
            padding: '0.4rem 0.2rem',
          }}
        >
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              style={{
                flexShrink: 0,
                width: '5.5rem', height: '3.5rem',
                position: 'relative', borderRadius: '0.4rem',
                overflow: 'hidden', cursor: 'pointer',
                border: i === idx ? `2px solid ${colors.gold}` : '2px solid transparent',
                padding: 0, background: 'none',
                opacity: i === idx ? 1 : 0.55,
                transition: 'opacity 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => { if (i !== idx) e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={e => { if (i !== idx) e.currentTarget.style.opacity = '0.55'; }}
              aria-label={`Go to image ${i + 1}`}
            >
              <Image src={src} alt={`Thumbnail ${i + 1}`} fill sizes="88px" style={{ objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Gallery mosaic ── */
export default function RoomGallery({ images, roomName }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const R = '1rem';
  const cell: React.CSSProperties = { borderRadius: R, overflow: 'hidden' };

  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.55fr 1fr',
        gridTemplateRows: 'auto auto',
        gap: '0.8rem',
        marginBottom: 'clamp(3rem,4vw,5rem)',
      }}>
        {/* Main — left, spans 2 rows */}
        <div
          onClick={() => setLightboxIndex(0)}
          className="group"
          style={{ position: 'relative', aspectRatio: '4/3', gridRow: 'span 2', cursor: 'pointer', ...cell }}
        >
          <Image
            src={images[0]}
            alt={roomName}
            fill
            sizes="(max-width:600px)100vw,55vw"
            className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            priority
          />
        </div>

        {/* Top right */}
        {images.length > 1 ? (
          <div
            onClick={() => setLightboxIndex(1)}
            className="group"
            style={{ position: 'relative', aspectRatio: '16/9', cursor: 'pointer', ...cell }}
          >
            <Image
              src={images[1]}
              alt={`${roomName} 2`}
              fill
              sizes="(max-width:600px)100vw,30vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </div>
        ) : (
          <div style={{ background: colors.bgAlt, aspectRatio: '16/9', ...cell }} />
        )}

        {/* Bottom right — View All */}
        <div
          onClick={() => setLightboxIndex(images.length > 2 ? 2 : 0)}
          style={{
            position: 'relative', aspectRatio: '16/9', cursor: 'pointer',
            background: colors.bgAlt,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${colors.goldPale}`,
            transition: 'background 0.2s ease',
            ...cell,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = colors.goldPale; }}
          onMouseLeave={e => { e.currentTarget.style.background = colors.bgAlt; }}
        >
          {images.length > 2 && (
            <Image
              src={images[2]}
              alt={`${roomName} 3`}
              fill
              sizes="(max-width:600px)100vw,30vw"
              style={{ objectFit: 'cover', opacity: 0.35 }}
            />
          )}
          <span style={{
            position: 'relative', zIndex: 1,
            fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 500,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: colors.navyDeep,
          }}>
            + View All
          </span>
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
